'use client';

import { useCallback, type MouseEvent, type ReactNode } from 'react';
import { useBarPanel } from './use-bar-panel';
import Link from 'next/link';
import { Menu, type LucideIcon } from 'lucide-react';
import { tw } from '@tw';
import { BAR_H, SHEET_ROW, barInk } from '@/components/ui/bar';
import { useOppositeGround } from './use-opposite-ground';

/**
 * The round control and the sheet it opens — the product's one menu.
 *
 * SiteBar mounts it in two shapes — the client's puck and the provider's
 * mobile burger — with different rows and ONE shell: as two separate menus
 * the hosts once drifted into two button sizes, two surfaces and a divider in
 * one of them, which is why the surface and the open and close behaviour live
 * here rather than with the rows.
 *
 * `children` is a function so a row can close the sheet after it acts — the
 * alternative is a context for one callback.
 */
export function AccountMenu({
  label,
  children,
}: {
  /** What the button announces to a screen reader. */
  label: string;
  children: (close: () => void) => ReactNode;
}) {
  const [button, flipped] = useOppositeGround<HTMLButtonElement>();
  // Back to the control that opened it, not to the top of the document.
  const { open, setOpen, wrap } = useBarPanel({
    onEscape: useCallback(() => button.current?.focus(), [button]),
  });

  return (
    <div ref={wrap} className="pointer-events-auto relative">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-label={label}
        aria-expanded={open}
        className={tw(
          BAR_H,
          'mm-btn mm-tile-pill relative flex w-11 items-center justify-center outline-none focus-visible:shadow-[inset_0_0_0_2px_currentColor]',
          barInk(flipped)
        )}
      >
        {/* A glyph, not an initial — an initial promises a profile, which is
            not what opens. */}
        <Menu className="h-5 w-5" strokeWidth={2.5} aria-hidden="true" />
      </button>

      {open && (
        // A taller list scrolls inside the sheet rather than growing past the
        // bottom of a phone. The gap between rows is the 4px the sheet already
        // pads itself by, so a row stands the same distance from its neighbour
        // as from the edge.
        <div className="mm-unfold absolute right-0 top-[3.25rem] flex max-h-[75dvh] w-[15rem] origin-top-right flex-col gap-1 overflow-y-auto rounded-[18px] bg-[var(--bar-raise)] p-1 shadow-[shadow:var(--bar-shadow)]">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/**
 * One line of the menu — literally the same shape whether it navigates, acts,
 * or is the page you are already on, built from `SHEET_ROW`. The current section is
 * NOT filled: the saturated colour is spent on the shortcut pill, the one thing
 * to press, and lighting up the row you are already standing on is the
 * quietest information the bar carries. `active` survives as `aria-current`,
 * which is where that fact belongs.
 *
 * Three slots, left to right: a glyph, the label, and the number the section
 * states about itself. The glyph is a scanning aid — the sheet is now the WHOLE
 * navigation, so a list of ten needs to be findable at a glance; it is never
 * the identifier, which is why no row is icon-only.
 */
export function MenuRow({
  href,
  onNavigate,
  onClick,
  active,
  icon,
  fact,
  children,
}: {
  /** A destination row. */
  href?: string;
  /** Its click — typed as the anchor's, because that is the element it lands
      on and the reselect helper needs the real event. */
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** An acting row (sign out). Never both. */
  onClick?: () => void;
  /** The section you are standing in — announced to assistive tech, never drawn. */
  active?: boolean;
  /** The leading glyph. */
  icon?: ReactNode;
  /** A number the section states about itself — held at the right edge so the
      figures line up into a column and the words truncate without them. */
  fact?: string;
  children: ReactNode;
}) {
  // `shrink-0`: past the sheet's scroll cap a row scrolls, it doesn't squash.
  const shape = tw(SHEET_ROW, 'w-full shrink-0 justify-start gap-3 text-left');

  const body = (
    <>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {fact && <span className="shrink-0 tabular-nums opacity-60">{fact}</span>}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={shape}
      >
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={shape}>
      {body}
    </button>
  );
}

/**
 * Where the bar hangs.
 *
 * Named here from the days of two hosts: the public bar opened at `pt-5`, the
 * dashboard's mobile header centred a 48px control inside an 80px row, so the
 * same button sat four pixels apart on two screens of the same product. The
 * second host is gone; the value stays the one place the bar's top lives —
 * the z-index and what sits beside it stay the host's own.
 */
// Off the top edge on purpose, and the padding gives back what the offset took
// (8 + 12 = the old 20): Safari 26 tints the status bar from a fixed element
// within a few px of the edge, and this one is transparent, so it sampled as
// black. Out of that window, the tint falls back to the body's own ground.
export const MENU_BAR_TOP = 'pointer-events-none fixed inset-x-0 top-2 pt-3';

/**
 * The glyph a menu row leads with. Full strength, not dimmed: it takes the
 * row's own colour through `currentColor`, so it is exactly as dark as the word
 * beside it and follows the theme without a token of its own.
 */
export function MenuGlyph({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />;
}
